import {
    RECIPE_TYPE_COOKING_STEP_URL,
    deleteFetchRequest,
    getFetchRequest,
    postFetchRequest,
} from '../store/requests';
import { errorCallback, makeUrl } from '../util';
import { CookingStep, CookingStepWrite } from './recipeTypes';

type StepsObserver = (steps: CookingStep[]) => void;

export type CookingStepSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export type CookingStepSaveState = {
    status: CookingStepSaveStatus;
    lastSavedAt: Date | null;
    error: unknown;
};

type SaveStateObserver = (state: CookingStepSaveState) => void;

type UpdateLocalOptions = {
    /** Default: debounce when patch is texts-only; false = save immediately */
    debounce?: boolean;
};

const TEXT_DEBOUNCE_MS = 700;

const cookingStepsUrl = (recipeType: string): string => (
    makeUrl([RECIPE_TYPE_COOKING_STEP_URL, recipeType, '/cookingStep/'])
);

const cookingStepUrl = (recipeType: string, stepId: string): string => makeUrl([
    RECIPE_TYPE_COOKING_STEP_URL,
    recipeType,
    '/cookingStep/',
    stepId,
    '/',
]);

const toWriteBody = (step: CookingStep | CookingStepWrite): CookingStepWrite => {
    const isReminder = Boolean(step.isReminder);
    const body: CookingStepWrite = {
        stepOrder: Number(step.stepOrder) || 0,
        stepCode: step.stepCode || '',
        measureBlock: step.measureBlock || 'none',
        isReminder,
        texts: (step.texts || []).map((text) => ({
            locale: text.locale,
            text: text.text || '',
        })),
        conditions: (step.conditions || []).map((condition) => ({
            conditionType: condition.conditionType,
            ingredientGroup: condition.ingredientGroup || '',
            fieldName: condition.fieldName || '',
            operator: condition.operator || '',
            value: condition.value || '',
            productCode: condition.productCode || '',
        })),
    };
    if (isReminder) {
        body.reminderKind = step.reminderKind || null;
        body.reminderPreset = step.reminderKind === 'preset' ? (step.reminderPreset || null) : null;
        body.reminderOffsetHours = step.reminderKind === 'offset_hours'
            ? (step.reminderOffsetHours ?? null)
            : null;
    }
    return body;
};

const shouldDebouncePatch = (patch: Partial<CookingStep>, options?: UpdateLocalOptions): boolean => {
    if (options?.debounce === true) {
        return true;
    }
    if (options?.debounce === false) {
        return false;
    }
    const keys = Object.keys(patch);
    if (keys.length !== 1) {
        return false;
    }
    return keys[0] === 'texts' || keys[0] === 'stepCode';
};

export const cookingStepSaveService = {
    recipeType: '' as string,
    steps: [] as CookingStep[],
    observers: [] as StepsObserver[],
    saveStateObservers: [] as SaveStateObserver[],

    saveStatus: 'idle' as CookingStepSaveStatus,
    lastSavedAt: null as Date | null,
    lastError: null as unknown,
    debounceTimer: null as ReturnType<typeof setTimeout> | null,
    pendingStepId: null as string | null,
    saveGenerationByStepId: {} as Record<string, number>,
    failedStepId: null as string | null,

    loadSteps(recipeType: string, callback?: StepsObserver): void {
        this.recipeType = recipeType;
        getFetchRequest(cookingStepsUrl(recipeType), (response: CookingStep[]) => {
            this.steps = Array.isArray(response) ? response : [];
            if (callback) {
                callback(this.steps);
            }
            this.notifyObservers();
        }, errorCallback);
    },

    getSteps(): CookingStep[] {
        return this.steps;
    },

    getSaveState(): CookingStepSaveState {
        return {
            status: this.saveStatus,
            lastSavedAt: this.lastSavedAt,
            error: this.lastError,
        };
    },

    registerObserver(observer: StepsObserver): void {
        this.observers.push(observer);
    },

    unRegisterObserver(observer: StepsObserver): void {
        this.observers = this.observers.filter((item) => item !== observer);
    },

    notifyObservers(): void {
        this.observers.forEach((observer) => observer(this.steps));
    },

    registerSaveStateObserver(observer: SaveStateObserver): void {
        this.saveStateObservers.push(observer);
        observer(this.getSaveState());
    },

    unRegisterSaveStateObserver(observer: SaveStateObserver): void {
        this.saveStateObservers = this.saveStateObservers.filter((item) => item !== observer);
    },

    notifySaveStateObservers(): void {
        const state = this.getSaveState();
        this.saveStateObservers.forEach((observer) => observer(state));
    },

    setSaveStatus(status: CookingStepSaveStatus, error: unknown = null): void {
        this.saveStatus = status;
        this.lastError = error;
        if (status === 'saved') {
            this.lastSavedAt = new Date();
            this.failedStepId = null;
        }
        this.notifySaveStateObservers();
    },

    createStep(step: CookingStepWrite, callback?: (response: CookingStep) => void): void {
        postFetchRequest(
            cookingStepsUrl(this.recipeType),
            toWriteBody(step),
            (response: CookingStep) => {
                this.loadSteps(this.recipeType, () => {
                    if (callback) {
                        callback(response);
                    }
                });
            },
            errorCallback,
        );
    },

    updateStep(step: CookingStep, generation?: number): void {
        if (!step.id) {
            return;
        }
        const stepId = step.id;
        const nextGeneration = generation ?? ((this.saveGenerationByStepId[stepId] || 0) + 1);
        this.saveGenerationByStepId[stepId] = nextGeneration;
        this.setSaveStatus('saving');

        postFetchRequest(
            cookingStepUrl(this.recipeType, stepId),
            toWriteBody(step),
            () => {
                if (this.saveGenerationByStepId[stepId] !== nextGeneration) {
                    return;
                }
                this.setSaveStatus('saved');
            },
            (error: unknown) => {
                if (this.saveGenerationByStepId[stepId] !== nextGeneration) {
                    return;
                }
                this.failedStepId = stepId;
                this.setSaveStatus('error', error);
                errorCallback(error);
            },
            true,
        );
    },

    deleteStep(stepId: string, callback?: StepsObserver): void {
        if (this.pendingStepId === stepId) {
            this.clearDebounceTimer();
            this.pendingStepId = null;
        }
        deleteFetchRequest(
            cookingStepUrl(this.recipeType, stepId),
            () => this.loadSteps(this.recipeType, callback),
            errorCallback,
        );
    },

    updateLocalStep(stepId: string, patch: Partial<CookingStep>, options?: UpdateLocalOptions): void {
        this.steps = this.steps.map((step) => (
            step.id === stepId ? { ...step, ...patch } : step
        ));
        this.notifyObservers();

        const updated = this.steps.find((step) => step.id === stepId);
        if (!updated) {
            return;
        }

        if (shouldDebouncePatch(patch, options)) {
            this.scheduleDebouncedSave(stepId);
            return;
        }

        this.clearDebounceTimer();
        this.pendingStepId = null;
        this.updateStep(updated);
    },

    scheduleDebouncedSave(stepId: string): void {
        this.pendingStepId = stepId;
        this.clearDebounceTimer();
        this.debounceTimer = setTimeout(() => {
            this.debounceTimer = null;
            const pendingId = this.pendingStepId;
            this.pendingStepId = null;
            if (!pendingId) {
                return;
            }
            const updated = this.steps.find((step) => step.id === pendingId);
            if (updated) {
                this.updateStep(updated);
            }
        }, TEXT_DEBOUNCE_MS);
    },

    clearDebounceTimer(): void {
        if (this.debounceTimer != null) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
    },

    flushPending(): void {
        const pendingId = this.pendingStepId;
        this.clearDebounceTimer();
        this.pendingStepId = null;
        if (!pendingId) {
            return;
        }
        const updated = this.steps.find((step) => step.id === pendingId);
        if (updated) {
            this.updateStep(updated);
        }
    },

    retryLastFailed(): void {
        if (!this.failedStepId) {
            return;
        }
        const updated = this.steps.find((step) => step.id === this.failedStepId);
        if (updated) {
            this.updateStep(updated);
        }
    },

    reorderSteps(orderedIds: string[]): void {
        this.flushPending();
        const byId = new Map(this.steps.map((step) => [step.id || '', step]));
        const nextSteps: CookingStep[] = [];
        const changed: CookingStep[] = [];

        orderedIds.forEach((id, index) => {
            const step = byId.get(id);
            if (!step) {
                return;
            }
            const stepOrder = (index + 1) * 10;
            const nextStep = step.stepOrder === stepOrder
                ? step
                : { ...step, stepOrder };
            nextSteps.push(nextStep);
            if (nextStep !== step) {
                changed.push(nextStep);
            }
        });

        this.steps = nextSteps;
        this.notifyObservers();
        changed.forEach((step) => {
            if (step.id) {
                this.updateStep(step);
            }
        });
    },
};
